import React, { useState } from 'react';
import { Flex } from 'antd';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { css } from 'styled-components';

import { trackEvent } from 'jf/analytics/Analytics';
import { MSTeamsUser } from 'jf/api';
import { useDevExTheme } from 'jf/common/themes/DevExTheme';
import { DevExButton } from 'jf/components/DevExButton';
import { DevExTag } from 'jf/components/DevExTag';
import { DevExText } from 'jf/components/DevExText';

const COLLAPSED_TARGET_COUNT = 10;

type MSTeamsTargetTagsProps = {
  msteamsTargets: MSTeamsUser[];
  unsavedMsteamsUsers?: MSTeamsUser[];
  onChange?: (msteamsUsers: MSTeamsUser[]) => void;
  isExpanding?: boolean;
};

export const MSTeamsTargetTags: React.FC<MSTeamsTargetTagsProps> = ({
  msteamsTargets,
  unsavedMsteamsUsers,
  onChange,
  isExpanding,
}) => {
  const [isTagListCollapsed, setIsTagListCollapsed] = useState(true);
  const theme = useDevExTheme();
  const targetCount = msteamsTargets?.length || 0;
  const unsavedUserCount = unsavedMsteamsUsers?.length || 0;

  return (
    <Flex vertical gap={theme.variable.spacing.sm}>
      {!!targetCount && (
        <DevExText type="secondary" fontSize="xs">
          {targetCount} target respondent
          {targetCount === 1 ? '' : 's'}
        </DevExText>
      )}
      <>
        <Flex gap={theme.variable.spacing.sm} wrap="wrap" align="center">
          {msteamsTargets
            .slice(0, isTagListCollapsed ? COLLAPSED_TARGET_COUNT : undefined)
            .map((target) => (
              <DevExTag key={target.id} color="purple">
                <div>{target.displayName}</div>
              </DevExTag>
            ))}
          {isTagListCollapsed && msteamsTargets.length > COLLAPSED_TARGET_COUNT && (
            <DevExButton
              size="small"
              type="text"
              onClick={() => setIsTagListCollapsed(false)}
              style={{ marginLeft: `-${theme.variable.spacing.sm}` }}
            >
              + {msteamsTargets.length - COLLAPSED_TARGET_COUNT} others
            </DevExButton>
          )}
        </Flex>

        {!!unsavedUserCount && (
          <DevExText type="secondary" fontSize="xs">
            {unsavedUserCount} new target respondent
            {unsavedUserCount === 1 ? '' : 's'}
          </DevExText>
        )}

        <Flex gap={theme.variable.spacing.sm} wrap="wrap" align="center">
          {unsavedMsteamsUsers &&
            unsavedMsteamsUsers.map((target) => {
              return (
                <DevExTag
                  key={target.id}
                  color="purple"
                  css={css`
                    &&&& {
                      padding-block: 0;
                      padding-right: ${theme.variable.spacing.xs};
                    }
                  `}
                >
                  <Flex align="center">
                    {target.displayName ?? target.name}
                    <DevExButton
                      icon={<FontAwesomeIcon icon={faXmark} />}
                      size="small"
                      type="text"
                      onClick={() => {
                        const removedTargets = [...(unsavedMsteamsUsers || [])].filter(
                          ({ id }) => id !== target.id
                        );
                        if (onChange) {
                          onChange(removedTargets);
                        }
                        trackEvent(`survey-analyzer:msteams-target-user:remove`, {
                          id: target.id,
                        });
                      }}
                    />
                  </Flex>
                </DevExTag>
              );
            })}

          {isExpanding && <DevExTag>Adding targets...</DevExTag>}

          {!!unsavedUserCount && !isExpanding && (
            <DevExButton
              size="small"
              type="text"
              onClick={() => {
                if (onChange) {
                  onChange([]);
                }
              }}
              style={{ marginLeft: `-${theme.variable.spacing.sm}` }}
            >
              Clear all
            </DevExButton>
          )}
        </Flex>
      </>
    </Flex>
  );
};
